import Typography from '@mui/material/Typography'
import React, { useEffect } from 'react'
import CustomAvatar from '../../../../avatar/Avatar'
import DialogCustom from '../../../../dialog/Dialog'
import SectionCollapse from '../../../../section/SectionCollapse'
import SurveyTemplatePreview from '../../Templates/SurveyTemplatePreview/SurveyTemplatePreview'
import InfoAboutData from '../InfoAboutData/InfoAboutData'
import FormatDate from '../../../../date/FormatDate'
import { Alert, Box, Skeleton, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material'
import { getSurveyAboutById } from '../../../../../services/data-entry/surveys.service'
import { handlerErrorV2 } from '../../../../../utils/errors'
import AvatarSkeleton from '../../../../avatar/AvatarSkeleton'
import useMySurveys from '../../../../../hooks/data-entry/useMySurver'
import { SURVEY_TYPE_ABOUT_CLASSES, SURVEY_TYPE_ABOUT_TEACHER } from '../../constants/typeSurvey'

// Survey types whose expected respondents (students) are known, so who is still pending can be listed
const SURVEYS_WITH_PENDING = [SURVEY_TYPE_ABOUT_CLASSES, SURVEY_TYPE_ABOUT_TEACHER]

const DialogPreviewAboutMe = ({ surveyId, recordId, open, onClose }) => {
  const [loading, setLoadingData] = React.useState(false)
  const [data, setData] = React.useState({})
  const [error, setError] = React.useState(null)
  const [diffStudents, setDiffStudents] = React.useState([])
  const { onShareLink } = useMySurveys()

  useEffect(() => {
    if (surveyId && recordId) {
      const onFetchData = async () => {
        setLoadingData(true)
        try {
          const response = await getSurveyAboutById(surveyId, recordId)
          if (!response?.data && response?.error) {
            setError(response.error.message || 'An error ocurred')
          } else {
            const formdata = response.data
            setData({
              ...formdata,
              questions: formdata?.template?.questions || []
            })
            setError(null)
            const studentsClass = formdata?.data?.students || []
            const diff = studentsClass?.filter((student) => {
              return !formdata?.results?.some((result) => result.code === student.code)
            })
            setDiffStudents(diff)
          }
        } catch (error) {
          const message = handlerErrorV2(error)
          setError(message)
        } finally {
          setLoadingData(false)
        }
      }
      onFetchData()
    }
  }, [surveyId, recordId])

  return (
    <DialogCustom
      id={`survey-preview-table-${data?.type || 'about'}-preview`}
      show={open}
      title='Preview'
      onClose={onClose}
      onCloseHeader={true}
      maxWidth='md'
    >
      {error && (
        <Alert severity='error' className='mb-4'>
          {error}
        </Alert>
      )}
      <div className='flex flex-col gap-2'>
        <InfoAboutData
          type={data?.type}
          data={{
            ...data?.data,
            survey: data?.name
          }}
          loading={loading}
          onShareLink={
            loading || data?.hasExpired ? undefined : () => onShareLink(surveyId, { _id: recordId })
          }
        />
        <SectionCollapse
          title='Survey Questions'
          classNameBody='max-h-screen-1/2-modal-content overflow-auto'
        >
          {loading ? (
            [...Array(5).keys()].map((item) => (
              <div key={item} className='py-2 mt-6'>
                <Skeleton variant='rectangular' width={'100%'} height={120} />
              </div>
            ))
          ) : (
            <SurveyTemplatePreview
              questions={data?.questions?.map((item) => {
                return {
                  ...item,
                  error: null
                }
              })}
              disabled={true}
            />
          )}
        </SectionCollapse>
        <SectionCollapse
          title={`Submitted (${data?.results?.length || 0})`}
          classNameBody=' overflow-auto relative'
        >
          {loading ? (
            <TableContainer className={`max-h-screen-1/2-modal-content`} component={Box}>
              <Table>
                <TableBody>
                  {[...Array(5).keys()].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          <AvatarSkeleton size='xsmall' />
                          <div className='flex flex-col'>
                            <Typography variant='body1'>
                              <Skeleton width={100} />
                            </Typography>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : data?.results?.length > 0 ? (
            <TableContainer className={`max-h-screen-1/2-modal-content`} component={Box}>
              <Table>
                <TableBody>
                  {data.results.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          <CustomAvatar src={`photos/${item?.photo}`} alt={item.code} size='xsmall' />
                          <div className='flex flex-col'>
                            <Typography variant='body1'>
                              {item.code} - {item.name}
                            </Typography>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell align='right'>
                        <Typography variant='body2' className='text-sm text-muted-foreground opacity-50'>
                          <FormatDate date={item.dateFilledOut} />
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography className=' text-muted-foreground opacity-50'>
              No users have responded to the survey yet.
            </Typography>
          )}
        </SectionCollapse>
        {SURVEYS_WITH_PENDING.includes(data?.type) && diffStudents?.length > 0 && (
          <SectionCollapse title={`Pending (${diffStudents?.length || 0})`}>
            <TableContainer className={`max-h-screen-1/2-modal-content`} component={Box}>
              <Table>
                <TableBody>
                  {diffStudents.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          <CustomAvatar src={`photos/${item?.photo}`} alt={item.code} size='xsmall' />
                          <div className='flex flex-col'>
                            <Typography variant='body1'>
                              {item.code} - {item.firstName} {item.lastName}
                            </Typography>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCollapse>
        )}
      </div>
    </DialogCustom>
  )
}

export default DialogPreviewAboutMe
